import React, { useState, useEffect } from 'react';
import { MapPin, Plus, Trash2, X, CheckCircle } from 'lucide-react';
import { employeeService } from '../../services/employeeService';

export default function EmployeeAddressesCard({ employeeUuid, canEdit = true }) {
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [formData, setFormData] = useState({
    address_type: 'current',
    line1: '',
    line2: '',
    city: '',
    state: '',
    country: 'India',
    postal_code: '',
    is_primary: false
  });

  const fetchAddresses = async () => {
    try {
      setLoading(true);
      const res = await employeeService.getEmployeeAddresses(employeeUuid);
      setAddresses(res.data || []);
    } catch (err) {
      console.error('Failed to load addresses', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (employeeUuid) fetchAddresses();
  }, [employeeUuid]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({ 
      ...formData, 
      [name]: type === 'checkbox' ? checked : value 
    });
  };

 const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await employeeService.addEmployeeAddress(employeeUuid, formData);
      setIsModalOpen(false);
      fetchAddresses();
      setFormData({
        address_type: 'current',
        line1: '',
        line2: '',
        city: '',
        state: '',
        country: 'India',
        postal_code: '',
        is_primary: false
      });
    } catch (err) {
      console.error('Error saving address:', err.response?.data || err);
      
      // Backend ka exact validation error message alert mein dikhane ke liye
      const errorMsg = err.response?.data?.message || JSON.stringify(err.response?.data?.errors) || 'Failed to save address.';
      alert(`Validation Error: ${errorMsg}`);
    }
  };

  const handleDelete = async (addressUuid) => {
    if (!window.confirm('Are you sure you want to delete this address?')) return;
    try {
      await employeeService.deleteEmployeeAddress(employeeUuid, addressUuid);
      fetchAddresses();
    } catch (err) {
      console.error('Error deleting address:', err);
      alert('Failed to delete address.');
    }
  };

  return (
    <div className="bg-white shadow-md rounded-lg p-6 flex flex-col gap-4">
      
      {/* Card Header matching theme */}
      <div className="flex items-center justify-between border-b pb-2 mb-2">
        <h3 className="text-lg font-semibold text-gray-800">Employee Addresses</h3>
        
        {canEdit === true && (
          <button
            onClick={() => setIsModalOpen(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-semibold transition cursor-pointer shadow-sm"
          >
            <Plus className="w-4 h-4" /> Add Address
          </button>
        )}
      </div>

      {/* Addresses List Grid */}
      {loading ? (
        <p className="text-xs text-gray-500 text-center py-4">Loading addresses...</p>
      ) : addresses.length === 0 ? (
        <p className="text-xs text-gray-500 text-center py-4">No addresses added yet.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {addresses.map((addr) => (
            <div key={addr.uuid} className="p-4 bg-gray-50 border border-gray-200 rounded-lg flex flex-col justify-between gap-3 shadow-2xs relative">
              
              <div>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    <div className="p-2 bg-blue-50 text-blue-600 rounded-lg border border-blue-100">
                      <MapPin className="w-4 h-4" />
                    </div>
                    <div>
                      <h4 className="text-xs font-bold text-gray-800 uppercase tracking-wide">
                        {addr.address_type} Address
                      </h4>
                      {addr.is_primary && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-emerald-50 text-emerald-600 border border-emerald-200 rounded text-[9px] font-bold uppercase mt-0.5">
                          <CheckCircle className="w-2.5 h-2.5" /> Primary
                        </span>
                      )}
                    </div>
                  </div>

                  {canEdit === true && (
                    <button 
                      onClick={() => handleDelete(addr.uuid)}
                      className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md transition cursor-pointer"
                      title="Delete Address"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>

                <div className="mt-3 space-y-1 text-xs text-gray-600">
                  <p className="font-semibold text-gray-800">{addr.line1}</p>
                  {addr.line2 && <p className="text-gray-600">{addr.line2}</p>}
                  <p className="text-gray-600">
                    {addr.city}, {addr.state} - <span className="font-mono text-gray-800">{addr.postal_code}</span>
                  </p>
                  <p className="text-gray-500 font-medium">{addr.country}</p>
                </div>
              </div>

            </div>
          ))}
        </div>
      )}

      {/* Add Address Modal */}
      {isModalOpen && canEdit === true && (
        <div className="fixed inset-0 z-50 bg-gray-900/40 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md border border-gray-200 overflow-hidden animate-in fade-in duration-200">
            
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-gray-50">
              <h4 className="text-sm font-bold text-gray-900">Add New Address</h4>
              <button onClick={() => setIsModalOpen(false)} className="p-1 text-gray-400 hover:text-gray-700 rounded-lg cursor-pointer">
                <X className="w-4 h-4" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 flex flex-col gap-3">
              <div>
                <label className="text-[10px] font-bold text-gray-500 uppercase">Address Type</label>
             <select 
  name="address_type" 
  value={formData.address_type} 
  onChange={handleChange}
  className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-lg text-xs font-semibold text-gray-800 focus:outline-blue-600 bg-white"
>
  <option value="current">Current</option>
  <option value="permanent">Permanent</option>
  <option value="temporary">Temporary</option>
  <option value="office">Office</option>
</select>
              </div>

              <div>
                <label className="text-[10px] font-bold text-gray-500 uppercase">Address Line 1</label>
                <input 
                  type="text" 
                  name="line1" 
                  placeholder="Flat/House No, Building Name" 
                  value={formData.line1} 
                  onChange={handleChange}
                  required
                  className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-lg text-xs font-semibold text-gray-800 focus:outline-blue-600"
                />
              </div>

              <div>
                <label className="text-[10px] font-bold text-gray-500 uppercase">Address Line 2 (Optional)</label>
                <input 
                  type="text" 
                  name="line2" 
                  placeholder="Street Name, Landmark" 
                  value={formData.line2} 
                  onChange={handleChange}
                  className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-lg text-xs font-semibold text-gray-800 focus:outline-blue-600"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-bold text-gray-500 uppercase">City</label>
                  <input 
                    type="text" 
                    name="city" 
                    placeholder="City" 
                    value={formData.city} 
                    onChange={handleChange}
                    required
                    className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-lg text-xs font-semibold text-gray-800 focus:outline-blue-600"
                  />
                </div>
                <div>
                  <label className="text-[10px] font-bold text-gray-500 uppercase">State</label>
                  <input 
                    type="text" 
                    name="state" 
                    placeholder="State" 
                    value={formData.state} 
                    onChange={handleChange}
                    required
                    className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-lg text-xs font-semibold text-gray-800 focus:outline-blue-600"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-bold text-gray-500 uppercase">Postal Code</label>
                  <input 
                    type="text" 
                    name="postal_code" 
                    placeholder="411001" 
                    value={formData.postal_code} 
                    onChange={handleChange}
                    required
                    className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-lg text-xs font-semibold text-gray-800 focus:outline-blue-600"
                  />
                </div>
                <div>
                  <label className="text-[10px] font-bold text-gray-500 uppercase">Country</label>
                  <input 
                    type="text" 
                    name="country" 
                    placeholder="India" 
                    value={formData.country} 
                    onChange={handleChange}
                    required
                    className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-lg text-xs font-semibold text-gray-800 focus:outline-blue-600"
                  />
                </div>
              </div>

              <div className="flex items-center gap-2 mt-1">
                <input 
                  type="checkbox" 
                  name="is_primary" 
                  id="is_primary"
                  checked={formData.is_primary} 
                  onChange={handleChange}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
                />
                <label htmlFor="is_primary" className="text-xs font-semibold text-gray-700 cursor-pointer">
                  Set as Primary Address
                </label>
              </div>

              <div className="flex items-center justify-end gap-2 pt-3 border-t border-gray-100 mt-2">
                <button 
                  type="button" 
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 border border-gray-200 text-gray-600 rounded-lg text-xs font-semibold hover:bg-gray-50 cursor-pointer"
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-semibold shadow-sm cursor-pointer"
                >
                  Save Address
                </button>
              </div>
            </form>

          </div>
        </div>
      )}

    </div>
  );
}